import {
  ApiPetProfileStatusEnum,
  ApiPetProfileStatusReasonEnum,
} from 'api/generated-client/PetProfile-Service-A.v1/models';
import { AvatarTypes } from '@chewy/pet-profile-pet-avatar';
import { PetInterface } from '../../types/pet';
import { getPetPossessivePronouns, getPetObjectPronouns } from '../../util/getPetPronouns';

export const getSharedCopy = (pet: PetInterface) => {
  const pronoun = getPetPossessivePronouns(pet.gender).toLowerCase();
  const contactPrompt = 'Need more support?';
  const contactLinkText = 'Reach out to us anytime.';
  const undoText = 'Undo';
  const restoreText = `Restore ${pronoun} profile`;

  return {
    contactPrompt,
    contactLinkText,
    undoText,
    restoreText,
  };
};

export type LandingStepCopy = {
  errorMessage: string;
  memorialTitle: string;
  memorialSubtitle: string;
  removeTitle: string;
  removeSubtitle: string;
};

export const getLandingStepCopy = (pet: PetInterface): LandingStepCopy => {
  const pronoun = getPetPossessivePronouns(pet.gender).toLowerCase();
  const errorMessage = `We're sorry, we couldn't update their profile. Please try again.`;
  const memorialTitle = 'Create a memorial';
  const memorialSubtitle = `Keep a memorial of ${pet.name}'s profile in your account. We won't personalize your experience with ${pronoun} details.`;
  const removeTitle = 'Remove profile';
  const removeSubtitle = `Remove ${pronoun} information from your account.`;

  return {
    errorMessage,
    memorialTitle,
    memorialSubtitle,
    removeTitle,
    removeSubtitle,
  };
};

export type ReasonStepCopy = {
  prompt: string;
  deceasedTitle: string;
  otherReasonTitle: string;
  optOutText: string;
  undoPrompt: string;
};

export const getReasonStepCopy = (
  pet: PetInterface,
  statusChoice?: ApiPetProfileStatusEnum,
): ReasonStepCopy => {
  const pronoun = getPetPossessivePronouns(pet.gender).toLowerCase();
  const promptVerb =
    statusChoice === ApiPetProfileStatusEnum.Deleted ? 'removing' : 'memorializing';

  const prompt = `We're sorry to hear that. May we ask why you're ${promptVerb} ${pet.name}'s profile?`;
  const deceasedTitle = `${pet.name} has passed`;
  const otherReasonTitle = 'Another reason not listed';
  const optOutText = "I'd rather not say";

  const undoPromptVerb =
    statusChoice === ApiPetProfileStatusEnum.Deleted ? 'removed' : 'memorialized';
  const undoPrompt = `We've ${undoPromptVerb} ${pronoun} profile`;

  return {
    prompt,
    deceasedTitle,
    otherReasonTitle,
    optOutText,
    undoPrompt,
  };
};

export type SympathyStepCopy = {
  headerText: string;
  bodyText1: string;
  bodyText2?: string;
  avatarStroke?: AvatarTypes;
  signatureText1: string;
  signatureText2: string;
  memorialButtonText: string;
  contactLinkTextPlain: string;
};

const getSympathyMessage = (
  ownerName: string,
  petName: string,
  reasonChoice: ApiPetProfileStatusReasonEnum,
): {
  headerText: string;
  bodyText1: string;
  bodyText2?: string;
  avatarStroke?: AvatarTypes;
} => {
  switch (reasonChoice) {
    case ApiPetProfileStatusReasonEnum.Deceased:
      return {
        headerText: `${ownerName}, we are so sorry for your loss`,
        bodyText1: `Losing such an important part of the family is never easy, but we hope you find comfort in the cherished memories you and ${petName} had together.`,
        bodyText2: `We're always here if you need anything.`,
        avatarStroke: AvatarTypes.MemorialDeceased,
      };
    case ApiPetProfileStatusReasonEnum.Other:
      return {
        headerText: `${ownerName}, thank you for letting us know`,
        bodyText1: `Please take care, and know that we're always here if you need anything.`,
      };
    case ApiPetProfileStatusReasonEnum.Unknown:
      return {
        headerText: `${ownerName}, thank you for sharing ${petName} with us`,
        bodyText1: `Please take care, and know that we're always here if you need anything.`,
      };
    // default to copy for UNKNOWN
    default:
      return {
        headerText: `${ownerName}, thank you for sharing ${petName} with us`,
        bodyText1: `Please take care, and know that we're always here if you need anything.`,
      };
  }
};

export const getSympathyStepCopy = (
  pet: PetInterface,
  reasonChoice: ApiPetProfileStatusReasonEnum,
): SympathyStepCopy => {
  const ownerFirstName = pet.ownerName ? pet.ownerName.split(' ')[0] : '';
  const pronoun = getPetPossessivePronouns(pet.gender).toLowerCase();

  const { headerText, bodyText1, bodyText2, avatarStroke } = getSympathyMessage(
    ownerFirstName,
    pet.name,
    reasonChoice,
  );
  const signatureText1 = `warmly,`;
  const signatureText2 = `your chewy family`;
  const memorialButtonText = `Visit ${pronoun} memorial`;
  const contactLinkTextPlain = 'Reach out to us anytime';

  return {
    headerText,
    bodyText1,
    bodyText2,
    avatarStroke,
    signatureText1,
    signatureText2,
    memorialButtonText,
    contactLinkTextPlain,
  };
};

export type ErrorPageCopy = {
  headerText: string;
  bodyText: string;
  contactButtonText: string;
  callButtonText: string;
  phoneNumber: string;
  emailButtonText: string;
};

export const getErrorPageCopy = (pet: PetInterface): ErrorPageCopy => {
  const headerText = `We're so sorry, but something went wrong on our end.`;
  const bodyText = `We're still here to help you with ${pet.name}'s profile. Please reach out to us anytime.`;
  const contactButtonText = `Contact Us`;
  const callButtonText = `Give us a call`;
  const phoneNumber = 'tel:+18006724399';
  const emailButtonText = `Send us an email`;

  return {
    headerText,
    bodyText,
    contactButtonText,
    callButtonText,
    phoneNumber,
    emailButtonText,
  };
};

export type UndoConfirmationCopy = {
  headerText: string;
  bodyText: string;
  profileButtonText: string;
  closeButtonText: string;
  memorializeText: string;
};

export const getUndoConfirmationCopy = (pet: PetInterface) => {
  const possPronoun = getPetPossessivePronouns(pet.gender).toLowerCase();
  const objectPronoun = getPetObjectPronouns(pet.gender).toLowerCase();

  const headerText = `We restored ${pet.name}'s profile`;
  const bodyText = `We'll use ${pet.name}'s profile to make better recommendations for ${objectPronoun}, track ${possPronoun} health, and celebrate ${possPronoun} unique traits.`;
  const profileButtonText = `Visit ${possPronoun} profile`;
  const closeButtonText = `Close`;
  const memorializeText = `Memorialize or remove profile`;

  return {
    headerText,
    bodyText,
    profileButtonText,
    closeButtonText,
    memorializeText,
  };
};
